import type { Dir } from '../core/padmap';

const FOCUSABLE =
  'button, a[href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

const visible = (el: HTMLElement): boolean => {
  if (el.closest('[hidden]')) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};

/** Is the middle of this element actually on top (not covered by another layer, not scrolled away)? */
const onTop = (el: HTMLElement): boolean => {
  const r = el.getBoundingClientRect();
  const x = r.left + r.width / 2;
  const y = r.top + r.height / 2;
  if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight) return false;
  const hit = document.elementFromPoint(x, y);
  return !!hit && (hit === el || el.contains(hit) || hit.contains(el));
};

const center = (el: HTMLElement): { x: number; y: number } => {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
};

const scrollable = (el: HTMLElement | null, stop: HTMLElement): HTMLElement | null => {
  for (let e = el; e && e !== stop.parentElement; e = e.parentElement) {
    const s = getComputedStyle(e);
    if (/(auto|scroll)/.test(s.overflowY) && e.scrollHeight > e.clientHeight + 2) return e;
  }
  return null;
};

/**
 * Controller navigation for the DOM menus: the D-pad moves focus to the nearest button in
 * that direction inside whichever dialog is on top, A presses it, the right stick scrolls.
 */
export class PadNav {
  /** The menu on top: the last modal dialog that isn't covered, or the title screen or creator. */
  private container(): HTMLElement | null {
    const all = [
      ...document.querySelectorAll<HTMLElement>(
        '[role="dialog"][aria-modal="true"], .title-screen, .creator-screen',
      ),
    ].filter(visible);
    for (let i = all.length - 1; i >= 0; i--) {
      const el = all[i] as HTMLElement;
      const c = center(el);
      const hit = document.elementFromPoint(c.x, c.y);
      if (hit && el.contains(hit)) return el;
    }
    return all.at(-1) ?? null;
  }

  private candidates(root: HTMLElement): HTMLElement[] {
    return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (el) => !(el as HTMLButtonElement).disabled && visible(el) && onTop(el),
    );
  }

  private focused(root: HTMLElement): HTMLElement | null {
    const a = document.activeElement as HTMLElement | null;
    return a && a !== root && root.contains(a) && visible(a) ? a : null;
  }

  private focus(el: HTMLElement): void {
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /** Make sure something in the open menu has focus, so A always does something visible. */
  ensure(): void {
    const root = this.container();
    if (!root || this.focused(root)) return;
    // Start on the menu's main button (even if it's scrolled out of view), else the first one.
    const primary = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].find(
      (el) => el.classList.contains('primary') && !(el as HTMLButtonElement).disabled && visible(el),
    );
    const first = primary ?? this.candidates(root)[0];
    if (first) this.focus(first);
  }

  move(dir: Dir): void {
    const root = this.container();
    if (!root) return;
    const cur = this.focused(root);
    if (!cur) return this.ensure();
    if (cur instanceof HTMLInputElement && cur.type === 'range' && (dir === 'left' || dir === 'right')) {
      if (dir === 'right') cur.stepUp();
      else cur.stepDown();
      cur.dispatchEvent(new Event('input', { bubbles: true }));
      cur.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    let next = this.nearest(cur, this.candidates(root), dir);
    if (!next && (dir === 'up' || dir === 'down')) {
      // The next thing might be scrolled out of view: scroll and look again.
      const box = scrollable(cur, root) ?? scrollable(root, root);
      if (box) {
        box.scrollBy({ top: (dir === 'down' ? 1 : -1) * box.clientHeight * 0.6 });
        next = this.nearest(cur, this.candidates(root), dir);
      }
    }
    if (next) this.focus(next);
  }

  private nearest(from: HTMLElement, list: HTMLElement[], dir: Dir): HTMLElement | null {
    const c = center(from);
    let best: HTMLElement | null = null;
    let bestScore = Infinity;
    for (const el of list) {
      if (el === from) continue;
      const p = center(el);
      const dx = p.x - c.x;
      const dy = p.y - c.y;
      const along = dir === 'down' ? dy : dir === 'up' ? -dy : dir === 'right' ? dx : -dx;
      const across = dir === 'down' || dir === 'up' ? Math.abs(dx) : Math.abs(dy);
      if (along < 4) continue;
      const score = along + across * 2.2;
      if (score < bestScore) {
        bestScore = score;
        best = el;
      }
    }
    return best;
  }

  accept(): void {
    const root = this.container();
    if (!root) return;
    const cur = this.focused(root);
    if (!cur) return this.ensure();
    if (cur instanceof HTMLInputElement && cur.type !== 'checkbox' && cur.type !== 'radio') return;
    if (cur instanceof HTMLTextAreaElement) return;
    cur.click();
  }

  scroll(dy: number): void {
    const root = this.container();
    if (!root || !dy) return;
    const box = scrollable(this.focused(root), root) ?? scrollable(root, root) ?? this.firstScrollable(root);
    box?.scrollBy({ top: dy });
  }

  private firstScrollable(root: HTMLElement): HTMLElement | null {
    for (const el of root.querySelectorAll<HTMLElement>('*')) {
      if (el.scrollHeight > el.clientHeight + 2 && /(auto|scroll)/.test(getComputedStyle(el).overflowY))
        return el;
    }
    return null;
  }
}
